import {
  JSX,
  createSignal,
  Accessor,
  onCleanup,
  splitProps,
  Show,
  createMemo,
  createComputed,
  Suspense,
  For,
  createEffect,
  createRoot,
  Signal,
} from 'solid-js';
import { Ref } from '@solid-primitives/refs';
import { createListTransition } from '@solid-primitives/transition-group';
import { createEventListener } from '@solid-primitives/event-listener';
import { Portal } from 'solid-js/web';
import gsap from 'gsap';

import Button from './Button';
import { setActiveElement } from './compatibility/ActiveScroll';
import { CustomErrorBoundary } from './ErrorBoundary';
import ScrollView from './Scrollview';
import { swipeArea } from '../utils';
import { createBottomSheetDrag } from './BottomSheet';
import { ActiveProvider } from './Active';
import { cx } from '../utils';
import { useConfig } from './Config';

const HEAD_SHAKE = 'head-shake';

// TODO: 这个需要fixed，暂时ignore
// eslint-disable-next-line
const [list, setList] = createSignal<PopItem[]>([]);

function createPopItem(
  jsx: Accessor<JSX.Element>,
  resovle: (value: any) => void,
  { close = true, onClose }: PopItemOptions = {}
) {
  return createRoot<PopItem>((dispose) => {
    const source = { value: 0 };

    const [val, setVal] = createSignal(source.value);
    let a: gsap.core.Tween | undefined;
    const ane: AneObj = {
      value: [
        val,
        ((value: number) => {
          if (diposed) return;
          source.value = value;
          setVal(value);
          a?.kill();
        }) as any,
      ],
      animateTo(value: number, onRemove) {
        if (diposed) return;
        if (onRemove) {
          diposed = true;
        }
        a = gsap.to(source, {
          value,
          duration: 0.35,
          overwrite: 'auto',
          ease: 'power3.out',
          onUpdate() {
            setVal(source.value);
          },
          onComplete() {
            onRemove?.();
            if (diposed) {
              dispose();
            }
          },
        });
      },
    };

    onCleanup(() => {
      a?.kill();
    });

    let diposed = false;

    const item: PopItem = {
      jsx,
      close,
      ane,
      onClose(value) {
        resovle(value);
        onClose?.(value);
      },
    };

    return item;
  });
}

export const pop: PopManager = {
  get list() {
    return list();
  },
  push(jsx, opt) {
    return new Promise((res) => {
      setList((pre) => [...pre, createPopItem(jsx, res, opt)]);
    });
  },
  confirm(jsx, opt) {
    return new Promise<boolean>((resolve) => {
      const children = () => (
        <Confirm
          {...opt}
          children={jsx as any}
          onConfirm={(v) => {
            resolve(v);
            pop.pop(children);
          }}
        />
      );
      pop.push(children, { close: false });
    });
  },
  alert(jsx, opt) {
    return pop.push(
      () => {
        const config = useConfig();
        return (
          <Pop close={false}>
            <div class="text-base text-secondary min-h-12">{jsx()}</div>
            <div class="flex mt-8 mb-2">
              <Button class="flex-1" onClick={() => pop.pop()} type="brand">
                {opt?.confirm || config.i18n.confirm}
              </Button>
            </div>
          </Pop>
        );
      },
      { close: false }
    );
  },
  prompt(jsx, opt) {
    return new Promise<string | undefined>((resolve) => {
      const children = () => {
        const input = createSignal(opt?.input);
        return (
          <Confirm
            {...opt}
            children={jsx(input)}
            onConfirm={(_v) => {
              resolve(input[0]());
              pop.pop(children);
            }}
          />
        );
      };
      pop.push(children, { close: false });
    });
  },
  pop(option) {
    setList((pre) => {
      if (pre.length === 0) return pre;
      const clone = [...pre];
      const item = option || clone[clone.length - 1].jsx;
      return clone.filter((it) => {
        const isTarget = it.jsx !== item;
        if (!isTarget) {
          return it.onClose();
        }
        return isTarget;
      });
    });
  },
};

export default function PopProvider() {
  const lastPop = () => {
    const arr = pop.list;
    return arr[arr.length - 1];
  };
  const transitionList = createListTransition(
    () => {
      return pop.list;
    },
    {
      onChange({ added, removed, finishRemoved }) {
        added.forEach((item) => item.ane.animateTo(1));
        removed.forEach((item) => item.ane.animateTo(0, () => finishRemoved([item])));
      },
    }
  );

  const [isScroll, setIsScroll] = createSignal(false);
  const visible = createMemo(() => transitionList().length > 0);
  return (
    <Portal
      // mount={document.getElementById('root-layer')!}
      ref={(el) => {
        el.className = 'pop-overlayer';
        const opacity = createMemo(() => pop.list[0]?.ane.value[0]() || 0);
        // 这里设置display需要模拟一个栈的情况，先调用effect的后cleanup才行
        // 目的是为了保证在任何获取dom信息之前被添加在页面上(block)，且在任何子cleanup之后再隐藏(none)
        // 这样才能最大限度解决一些dom的信息获取，主要是父元素none后子元素将无法获取scrollTop等信息
        createComputed(() => {
          if (visible()) {
            el.style.visibility = 'visible';
            setActiveElement(el, true);
          }
        });
        createEffect(() => {
          if (visible()) {
            onCleanup(() => {
              el.style.visibility = 'hidden';
            });
          }
        });
        createComputed(() => {
          el.style.backgroundColor = `rgba(16, 18, 18, ${opacity() * 0.8})`;
        });

        function shake() {
          const lastEl = el.children[el.children.length - 1];
          if (!lastEl) return;

          if (lastEl.className.indexOf(HEAD_SHAKE) === -1) {
            lastEl.classList.toggle(HEAD_SHAKE);
            function aneEnd() {
              lastEl.classList.toggle(HEAD_SHAKE);
              lastEl.removeEventListener('animationend', aneEnd);
            }
            lastEl.addEventListener('animationend', aneEnd);
          }
        }

        createEventListener(el, 'click', (e: any) => {
          if (e.target !== el) return;
          const last = lastPop();
          if (!last) return;
          if (last.close) {
            pop.pop();
          } else {
            shake();
          }
        });
      }}
    >
      <div
        class={cx('absolute left-0 h-full z-10', isScroll() ? 'pointer-events-auto' : 'pointer-events-none')}
        style={{
          width: `${swipeArea}px`,
        }}
      />
      <For each={transitionList()}>{(it) => <PopAne item={it} onScroll={setIsScroll} />}</For>
    </Portal>
  );
}

interface AneObj {
  value: Signal<number>;
  animateTo(value: number, onRemove?: (...args: any[]) => any): void;
}

function PopAne(props: { item: PopItem; onScroll(scroll: boolean): void }) {
  return (
    <ActiveProvider value={props.item === pop.list[pop.list.length - 1]}>
      <CustomErrorBoundary
        fallback={(_, __, defaultFallback) => {
          return (
            <Pop>
              <div>{defaultFallback}</div>
            </Pop>
          );
        }}
      >
        <Ref
          ref={(rel) => {
            const el = rel as HTMLDivElement;
            if (!el) return;
            const isBs = !el.classList.contains('pop-center');
            const [process] = props.item.ane.value;
            createComputed(
              isBs
                ? () => {
                    el.style.transform = `translate3d(0, ${(1 - process()) * 100}%, 0)`;
                  }
                : () => {
                    el.style.opacity = String(process());
                    el.style.transform = `scale(${process() * 0.2 + 0.8})`;
                  }
            );
            if (isBs) {
              const isDrag = createBottomSheetDrag(
                () => el,
                props.item.ane.value,
                (isClose) => {
                  if (isClose) {
                    pop.pop();
                  } else {
                    props.item.ane.animateTo(1);
                  }
                }
              );
              /** 监听drag状态预测滚动状态
               * 这是ios的一个异常bug，在左边区域大概屏幕宽度0.07位置滑动会触发手势返回，
               * 但是ios为了优化滚动，在滚动过程中的容器是不会响应touch相关的事件的，导致swipe back无法阻止。
               * 所以添加这一层来过滤掉这个事件，防止异常返回。
               */
              createComputed((pre) => {
                const drag = isDrag();
                if (pre && !drag) {
                  props.onScroll(true);
                  const timer = setTimeout(() => props.onScroll(false), 2000);
                  onCleanup(() => clearTimeout(timer));
                }
                return drag;
              });
            }
          }}
        >
          {props.item.jsx()}
        </Ref>
      </CustomErrorBoundary>
    </ActiveProvider>
  );
}

interface PopProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'title'> {
  type?: 'bottomsheet' | 'center';
  close?: boolean;
  title?: JSX.Element;
}
export function Pop(props: PopProps) {
  const [, others] = splitProps(props, ['type', 'title', 'children', 'class', 'close']);
  const config = useConfig();
  let scrollEl!: HTMLDivElement;

  return (
    <div
      {...others}
      class={cx(
        'bg-layer5-card dark:bg-layer2 pop-center',
        props.title || props.close !== false ? 'pop-with-title' : '',
        props.class
      )}
    >
      <Show when={props.title}>
        <div class="pop-title text-neutral-main1 bg-layer3-card">{props.title}</div>
      </Show>
      <Show when={props.close !== false}>
        <Button
          onClick={() => pop.pop()}
          class="pop-close p-0 w-8 h-8 flex items-center justify-center hover:bg-alpha-autowhite-a5 rounded-full transition-all duration-300 hover:text-brand-default"
        >
          <config.icons.close />
        </Button>
      </Show>

      <ScrollView
        ref={scrollEl}
        // style={{ height: height() }}
        class="pop-content "
      >
        <Suspense fallback={<BarLoading class="min-h-40 size-full flex-1 center" />}>{props.children}</Suspense>
      </ScrollView>
    </div>
  );
}

function Confirm(props: {
  children?: JSX.Element;
  title?: JSX.Element;
  cancel?: JSX.Element;
  confirm?: JSX.Element;
  onConfirm(value: boolean): void;
}) {
  const config = useConfig();
  onCleanup(() => {
    props.onConfirm(false);
  });
  return (
    <Pop class="" close={false}>
      <div class="text-base  text-secondary min-h-12">{props.children}</div>
      <div class="flex mt-8 mb-2">
        <Button class="flex-1 min-h-11 mr-3" type="second" onClick={() => props.onConfirm(false)}>
          {props.cancel || config.i18n.cancel}
        </Button>
        <Button class="flex-1 min-h-11" onClick={() => props.onConfirm(true)} type="brand">
          {props.confirm || config.i18n.confirm}
        </Button>
      </div>
    </Pop>
  );
}

interface PopItem {
  jsx: Accessor<JSX.Element>;
  /** 是否允许关闭, 如果不允许则不能点击overlayer进行关闭，需要手动调用api进行关闭 */
  close: boolean;
  ane: AneObj;
  onClose(value?: any): void;
}

type PopItemOptions = Partial<Omit<PopItem, 'jsx'>>;

interface PopManager {
  get list(): PopItem[];
  push(jsx: Accessor<JSX.Element>, option?: PopItemOptions): Promise<any>;
  confirm(jsx: Accessor<JSX.Element>, option?: ConfirmOption): Promise<boolean>;
  alert(jsx: Accessor<JSX.Element>, option?: AlertOption): Promise<void>;
  prompt(jsx: (input: Signal<string | undefined>) => JSX.Element, option?: PromptOption): Promise<string | undefined>;
  pop(com?: Accessor<JSX.Element>): void;
}

interface AlertOption {
  icon?: JSX.Element;
  confirm?: JSX.Element;
}

interface ConfirmOption extends AlertOption {
  cancel?: JSX.Element;
}

interface PromptOption extends ConfirmOption {
  input?: string;
}
